import { useEffect, useState, useCallback, useRef } from "react";
import { get, put } from "../../services/smartApiService";
import { MIRROR_URLS, KITE2_URLS } from "../../api/MirrorUrls";
import { KITE_URLS } from "../../api/KiteUrls";
import { useSiteContext } from "../../contexts/SiteProvider";

// ─── Types ───────────────────────────────────────────────────────────────────

interface Position {
  tradingsymbol: string;
  exchange: string;
  product: string;
  quantity: number;
  average_price: number;
  last_price: number;
  pnl: number;
  unrealised: number;
  realised: number;
}

interface Order {
  order_id: string;
  tradingsymbol: string;
  exchange: string;
  transaction_type: string;
  order_type: string;
  product: string;
  quantity: number;
  price: number;
  average_price: number;
  filled_quantity: number;
  status: string;
  status_message: string;
  order_timestamp: string;
}

interface MirrorEntry {
  source_order_id: string;
  dest_order_id: string | null;
  tradingsymbol: string;
  exchange: string;
  transaction_type: string;
  source_qty: number;
  mirror_pct_applied: number;
  lot_size_used: number | null;
  dest_qty: number | null;
  source_status: string;
  dest_status: string | null;
  mirror_status: string;
  mirror_error: string | null;
  created_at: string | null;
}

interface AccountStatus {
  is_connected: boolean;
  user_id?: string;
}

interface MirrorConfig {
  is_enabled: boolean;
  mirror_pct: number;
  lot_sizes: { symbol_prefix: string; exchange: string; lot_size: number }[];
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const fmt = (n: number) =>
  new Intl.NumberFormat("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);

const fmtTime = (ts: string) => {
  if (!ts) return "—";
  try { return new Date(ts).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit" }); }
  catch { return ts; }
};

const pnlColor = (v: number) => (v >= 0 ? "#26a65b" : "#e74c3c");

const ORDER_STATUS_COLOR: Record<string, string> = {
  COMPLETE:        "#26a65b",
  OPEN:            "#4fc3f7",
  CANCELLED:       "#888",
  REJECTED:        "#e74c3c",
  TRIGGER_PENDING: "#f39c12",
};

const MIRROR_STATUS_COLOR: Record<string, string> = {
  MIRRORED:          "#26a65b",
  FAILED:            "#e74c3c",
  SKIPPED:           "#666",
  DISABLED:          "#f39c12",
  CANCELLED_ON_DEST: "#9b59b6",
};

const badge = (color: string, text: string) => (
  <span style={{
    background: `${color}22`, color, border: `1px solid ${color}44`,
    borderRadius: 4, padding: "2px 7px", fontSize: 11, fontWeight: 600,
  }}>{text}</span>
);

// ─── Stat Card ───────────────────────────────────────────────────────────────

const StatCard = ({ label, value, sub, color }: {
  label: string; value: string; sub?: string; color?: string;
}) => (
  <div style={{
    background: "#0d0d1a", border: "1px solid #1a1a2e", borderRadius: 8,
    padding: "12px 14px", flex: 1, minWidth: 100,
  }}>
    <div style={{ fontSize: 10, color: "#555", textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>{label}</div>
    <div style={{ fontSize: 18, fontWeight: 700, color: color || "#e8e8f0" }}>{value}</div>
    {sub && <div style={{ fontSize: 10, color: "#444", marginTop: 2 }}>{sub}</div>}
  </div>
);

// ─── Account Panel ───────────────────────────────────────────────────────────

type PanelTab = "positions" | "orders";

const AccountPanel = ({
  title, accentColor, status, loginUrl, positions, orders, onRefreshStatus,
}: {
  title: string;
  accentColor: string;
  status: AccountStatus | null;
  loginUrl: string;
  positions: Position[];
  orders: Order[];
  onRefreshStatus: () => void;
}) => {
  const [tab, setTab] = useState<PanelTab>("positions");

  const openPositions  = positions.filter(p => p.quantity !== 0);
  const closedPositions = positions.filter(p => p.quantity === 0);
  const [showClosed, setShowClosed] = useState(false);

  const openOrders  = orders.filter(o => o.status === "OPEN" || o.status === "TRIGGER_PENDING").length;
  const filledOrders = orders.filter(o => o.status === "COMPLETE").length;
  const totalPnl = positions.reduce((s, p) => s + (p.pnl || 0), 0);

  const connected = status?.is_connected;

  return (
    <div style={{
      flex: 1, minWidth: 0,
      border: `1px solid ${connected ? accentColor + "44" : "#1e1e2e"}`,
      borderRadius: 10, background: "#10101a", display: "flex", flexDirection: "column",
    }}>
      {/* Panel header */}
      <div style={{
        padding: "12px 16px",
        borderBottom: "1px solid #1a1a2e",
        background: "#0d0d1a",
        borderRadius: "10px 10px 0 0",
        display: "flex", alignItems: "center", justifyContent: "space-between",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{
            width: 8, height: 8, borderRadius: "50%", flexShrink: 0,
            background: connected ? accentColor : "#e74c3c",
            boxShadow: connected ? `0 0 6px ${accentColor}` : "none",
          }} />
          <span style={{ fontSize: 13, fontWeight: 700, color: "#e8e8f0" }}>{title}</span>
          {status?.user_id && (
            <span style={{ fontSize: 11, color: "#555" }}>({status.user_id})</span>
          )}
        </div>
        {!connected ? (
          <a href={loginUrl} style={{
            background: "#1a2a3a", border: `1px solid ${accentColor}44`,
            borderRadius: 5, color: accentColor, padding: "4px 12px",
            fontSize: 12, textDecoration: "none", fontWeight: 600,
          }}>
            Connect →
          </a>
        ) : (
          <button onClick={onRefreshStatus} style={{
            background: "transparent", border: "none", color: "#444",
            fontSize: 11, cursor: "pointer",
          }}>↺</button>
        )}
      </div>

      {/* Stat cards */}
      <div style={{ display: "flex", gap: 8, padding: "12px 14px", flexWrap: "wrap" }}>
        <StatCard
          label="Net P&L"
          value={`${totalPnl >= 0 ? "+" : ""}₹${fmt(totalPnl)}`}
          color={pnlColor(totalPnl)}
          sub="unrealised + realised"
        />
        <StatCard
          label="Open Pos"
          value={String(openPositions.length)}
          sub={`${closedPositions.length} closed`}
          color={accentColor}
        />
        <StatCard
          label="Open Orders"
          value={String(openOrders)}
          sub={`${filledOrders} filled`}
          color={openOrders > 0 ? "#f39c12" : "#888"}
        />
        <StatCard
          label="Total Orders"
          value={String(orders.length)}
          sub="today"
          color="#888"
        />
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", borderBottom: "1px solid #1a1a2e", padding: "0 14px" }}>
        {(["positions", "orders"] as PanelTab[]).map(t => (
          <button key={t} onClick={() => setTab(t)} style={{
            background: "none", border: "none", cursor: "pointer",
            padding: "8px 14px", fontSize: 12, fontWeight: 600,
            color: tab === t ? accentColor : "#555",
            borderBottom: tab === t ? `2px solid ${accentColor}` : "2px solid transparent",
            transition: "all 0.15s", textTransform: "capitalize",
          }}>
            {t === "positions" && `Positions (${openPositions.length})`}
            {t === "orders"    && `Orders (${orders.length})`}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div style={{ flex: 1, overflowX: "auto" }}>
        {tab === "positions" && (
          <>
            {/* closed toggle */}
            {closedPositions.length > 0 && (
              <div style={{ padding: "8px 14px", borderBottom: "1px solid #1a1a2e" }}>
                <button onClick={() => setShowClosed(s => !s)} style={{
                  background: "transparent", border: "1px solid #2a2a3e",
                  borderRadius: 4, color: "#555", padding: "3px 10px",
                  fontSize: 11, cursor: "pointer",
                }}>
                  {showClosed ? "Hide closed" : `Show ${closedPositions.length} closed`}
                </button>
              </div>
            )}
            {positions.length === 0 ? (
              <div style={emptyStyle}>No positions</div>
            ) : (
              <table style={tableStyle}>
                <thead><tr>
                  {["Symbol", "Qty", "Avg", "LTP", "P&L", "P&L%"].map(h => (
                    <th key={h} style={thStyle}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {(showClosed ? positions : openPositions).map((p, i) => {
                    const closed = p.quantity === 0;
                    const pct = p.average_price > 0
                      ? ((p.last_price - p.average_price) / p.average_price) * 100 : 0;
                    return (
                      <tr key={i} style={{ ...(i % 2 === 0 ? rowAlt : {}), opacity: closed ? 0.4 : 1 }}>
                        <td style={{ ...tdStyle, fontWeight: 600, color: "#e8e8f0", fontSize: 12 }}>
                          {p.tradingsymbol}
                          {closed && <span style={{ marginLeft: 5, fontSize: 9, color: "#555", border: "1px solid #333", borderRadius: 3, padding: "1px 4px" }}>CLOSED</span>}
                        </td>
                        <td style={{ ...tdStyle, color: closed ? "#555" : p.quantity > 0 ? "#26a65b" : "#e74c3c", fontWeight: 600 }}>
                          {closed ? "0" : `${p.quantity > 0 ? "+" : ""}${p.quantity}`}
                        </td>
                        <td style={tdStyle}>{fmt(p.average_price)}</td>
                        <td style={{ ...tdStyle, color: "#4fc3f7" }}>{fmt(p.last_price)}</td>
                        <td style={{ ...tdStyle, color: pnlColor(p.pnl), fontWeight: 600 }}>
                          {p.pnl >= 0 ? "+" : ""}₹{fmt(p.pnl)}
                        </td>
                        <td style={{ ...tdStyle, color: pnlColor(pct) }}>
                          {pct >= 0 ? "+" : ""}{pct.toFixed(2)}%
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </>
        )}

        {tab === "orders" && (
          orders.length === 0 ? (
            <div style={emptyStyle}>No orders today</div>
          ) : (
            <table style={tableStyle}>
              <thead><tr>
                {["Time", "Symbol", "Type", "Qty", "Price", "Status"].map(h => (
                  <th key={h} style={thStyle}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {[...orders].reverse().map((o, i) => (
                  <tr key={i} style={i % 2 === 0 ? rowAlt : {}}>
                    <td style={{ ...tdStyle, color: "#666", fontSize: 11 }}>{fmtTime(o.order_timestamp)}</td>
                    <td style={{ ...tdStyle, fontWeight: 600, color: "#e8e8f0", fontSize: 12 }}>{o.tradingsymbol}</td>
                    <td style={tdStyle}>
                      <span style={{ color: o.transaction_type === "BUY" ? "#26a65b" : "#e74c3c", fontWeight: 700, fontSize: 12 }}>
                        {o.transaction_type}
                      </span>
                    </td>
                    <td style={tdStyle}>{o.filled_quantity}/{o.quantity}</td>
                    <td style={tdStyle}>{o.price > 0 ? fmt(o.price) : "MKT"}</td>
                    <td style={tdStyle}>{badge(ORDER_STATUS_COLOR[o.status] || "#555", o.status)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        )}
      </div>
    </div>
  );
};

// ─── Mirror Log Table ─────────────────────────────────────────────────────────

const MirrorLogTable = ({ entries }: { entries: MirrorEntry[] }) => {
  if (entries.length === 0) {
    return <div style={emptyStyle}>No mirror activity today</div>;
  }
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={tableStyle}>
        <thead><tr>
          {["Symbol", "Type", "Src Qty", "Pct", "Lot", "Dest Qty", "Src Status", "Dest Status", "Mirror Status", "Error"].map(h => (
            <th key={h} style={thStyle}>{h}</th>
          ))}
        </tr></thead>
        <tbody>
          {entries.map((m, i) => (
            <tr key={i} style={i % 2 === 0 ? rowAlt : {}}>
              <td style={{ ...tdStyle, fontWeight: 600, color: "#e8e8f0" }}>{m.tradingsymbol}</td>
              <td style={tdStyle}>
                <span style={{ color: m.transaction_type === "BUY" ? "#26a65b" : "#e74c3c", fontWeight: 700 }}>
                  {m.transaction_type || "—"}
                </span>
              </td>
              <td style={tdStyle}>{m.source_qty}</td>
              <td style={{ ...tdStyle, color: "#666" }}>{m.mirror_pct_applied > 0 ? `${m.mirror_pct_applied}%` : "—"}</td>
              <td style={{ ...tdStyle, color: "#555" }}>{m.lot_size_used ?? "—"}</td>
              <td style={{ ...tdStyle, fontWeight: 600 }}>{m.dest_qty ?? "—"}</td>
              <td style={tdStyle}>{badge(ORDER_STATUS_COLOR[m.source_status] || "#555", m.source_status)}</td>
              <td style={tdStyle}>{m.dest_status ? badge(ORDER_STATUS_COLOR[m.dest_status] || "#555", m.dest_status) : <span style={{ color: "#333" }}>—</span>}</td>
              <td style={tdStyle}>{badge(MIRROR_STATUS_COLOR[m.mirror_status] || "#555", m.mirror_status)}</td>
              <td style={{ ...tdStyle, color: "#e74c3c", fontSize: 11, maxWidth: 180 }}>
                {m.mirror_error ? (
                  <span title={m.mirror_error} style={{ cursor: "help" }}>
                    {m.mirror_error.length > 40 ? m.mirror_error.slice(0, 40) + "…" : m.mirror_error}
                  </span>
                ) : "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────

const MirrorPage = () => {
  const { setTitle } = useSiteContext();

  const [data, setData]               = useState<any>(null);
  const [config, setConfig]           = useState<MirrorConfig | null>(null);
  const [src1Status, setSrc1Status]   = useState<AccountStatus | null>(null);
  const [kite2Status, setKite2Status] = useState<AccountStatus | null>(null);
  const [initialLoad, setInitialLoad] = useState(true);
  const [syncing, setSyncing]         = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [pctInput, setPctInput]       = useState("10");
  const [savingPct, setSavingPct]     = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const firstFetch = useRef(true);

  useEffect(() => { setTitle("Order Mirror"); }, []);

  const fetchStatuses = useCallback(() => {
    get(KITE_URLS.STATUS).subscribe({
      next: (r: any) => setSrc1Status({ is_connected: r.data?.is_connected, user_id: r.data?.user_id }),
      error: () => setSrc1Status({ is_connected: false }),
    });
    get(KITE2_URLS.STATUS).subscribe({
      next: (r: any) => setKite2Status({ is_connected: r.data?.is_connected, user_id: r.data?.user_id }),
      error: () => setKite2Status({ is_connected: false }),
    });
  }, []);

  const fetchConfig = useCallback(() => {
    get(MIRROR_URLS.CONFIG).subscribe({
      next: (r: any) => {
        setConfig(r.data);
        setPctInput(String(r.data?.mirror_pct ?? 10));
      },
      error: () => {},
    });
  }, []);

  const fetchData = useCallback(() => {
    if (firstFetch.current) setInitialLoad(true);
    else setSyncing(true);
    get(MIRROR_URLS.TODAY).subscribe({
      next: (r: any) => {
        setData(r.data);
        setInitialLoad(false);
        setSyncing(false);
        setLastUpdated(new Date());
        firstFetch.current = false;
      },
      error: () => { setInitialLoad(false); setSyncing(false); firstFetch.current = false; },
    });
  }, []);

  useEffect(() => {
    fetchStatuses();
    fetchConfig();
    fetchData();
    timerRef.current = setInterval(fetchData, 15_000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [fetchData, fetchStatuses, fetchConfig]);

  const handleToggle = () => {
    if (!config) return;
    const next = !config.is_enabled;
    put(MIRROR_URLS.CONFIG, { is_enabled: next }).subscribe({
      next: () => setConfig(c => c ? { ...c, is_enabled: next } : c),
      error: () => {},
    });
  };

  const handleSavePct = () => {
    const pct = parseFloat(pctInput);
    if (isNaN(pct) || pct < 1 || pct > 100) return;
    setSavingPct(true);
    put(MIRROR_URLS.CONFIG, { mirror_pct: pct }).subscribe({
      next: () => { setConfig(c => c ? { ...c, mirror_pct: pct } : c); setSavingPct(false); },
      error: () => setSavingPct(false),
    });
  };

  const srcOrders:   Order[]       = data?.source_orders    || [];
  const dstOrders:   Order[]       = data?.dest_orders      || [];
  const srcPos:      Position[]    = data?.source_positions || [];
  const dstPos:      Position[]    = data?.dest_positions   || [];
  const mirrorMap:   MirrorEntry[] = data?.mirror_map       || [];
  const poller                     = data?.poller           || {};

  const totalMirrored = mirrorMap.filter(m => m.mirror_status === "MIRRORED").length;
  const failed        = mirrorMap.filter(m => m.mirror_status === "FAILED").length;

  return (
    <div style={{ padding: "18px 20px", minHeight: "100vh", background: "#07070f", color: "#e8e8f0" }}>

      {/* ── Header ── */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>🔁 Order Mirror</h2>
          <div style={{ fontSize: 11, color: "#444", marginTop: 2 }}>
            NIFTY (NFO) &amp; SENSEX (BFO) options · backend polls every 15s
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          {/* Market status */}
          <span style={{ fontSize: 11, display: "flex", alignItems: "center", gap: 5 }}>
            <span style={{
              width: 7, height: 7, borderRadius: "50%",
              background: poller.market_hours ? "#26a65b" : "#2a2a2a",
              boxShadow: poller.market_hours ? "0 0 5px #26a65b" : "none",
            }} />
            <span style={{ color: "#555" }}>{poller.market_hours ? "Market open" : "Market closed"}</span>
          </span>

          {/* Mirror toggle */}
          <button onClick={handleToggle} style={{
            background: config?.is_enabled ? "#0d2e1a" : "#1a1a1a",
            border: `1px solid ${config?.is_enabled ? "#26a65b" : "#333"}`,
            borderRadius: 20, padding: "5px 16px", cursor: "pointer",
            color: config?.is_enabled ? "#26a65b" : "#555", fontWeight: 700, fontSize: 12,
          }}>
            {config?.is_enabled ? "● Mirror ON" : "○ Mirror OFF"}
          </button>

          {/* Qty % */}
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontSize: 12, color: "#666" }}>Dest %</span>
            <input
              type="number" min={1} max={100} value={pctInput}
              onChange={e => setPctInput(e.target.value)}
              style={{
                width: 52, background: "#1a1a2e", border: "1px solid #2a2a3e",
                borderRadius: 5, color: "#e8e8f0", padding: "4px 8px",
                fontSize: 13, textAlign: "center",
              }}
            />
            <button onClick={handleSavePct} disabled={savingPct} style={{
              background: "#1a2a1a", border: "1px solid #26a65b44", borderRadius: 5,
              color: "#26a65b", padding: "4px 10px", fontSize: 12, cursor: "pointer",
              opacity: savingPct ? 0.5 : 1,
            }}>
              Save
            </button>
          </div>

          {/* Sync indicator */}
          {lastUpdated && (
            <span style={{ fontSize: 11, color: syncing ? "#4fc3f7" : "#444" }}>
              {syncing ? "⟳ syncing" : `↺ ${lastUpdated.toLocaleTimeString("en-IN")}`}
            </span>
          )}

          <button onClick={fetchData} disabled={syncing || initialLoad} style={{
            background: "#1e1e2e", border: "1px solid #2a2a3e", borderRadius: 6,
            color: "#4fc3f7", padding: "6px 12px", fontSize: 12, cursor: "pointer",
            opacity: syncing || initialLoad ? 0.5 : 1,
          }}>↺ Refresh</button>
        </div>
      </div>

      {/* ── Summary strip ── */}
      <div style={{
        display: "flex", gap: 10, marginBottom: 16,
        background: "#10101a", border: "1px solid #1a1a2e", borderRadius: 8, padding: "10px 16px",
        flexWrap: "wrap", alignItems: "center",
      }}>
        <span style={{ fontSize: 12, color: "#555", marginRight: 4 }}>Today:</span>
        <span style={{ fontSize: 13, color: "#26a65b", fontWeight: 600 }}>✅ {totalMirrored} mirrored</span>
        {failed > 0 && <span style={{ fontSize: 13, color: "#e74c3c", fontWeight: 600 }}>❌ {failed} failed</span>}
        <span style={{ fontSize: 13, color: "#666" }}>⊘ {mirrorMap.filter(m => m.mirror_status === "SKIPPED").length} skipped</span>
        <span style={{ fontSize: 13, color: "#9b59b6" }}>↩ {mirrorMap.filter(m => m.mirror_status === "CANCELLED_ON_DEST").length} cancelled</span>
        {config?.lot_sizes && config.lot_sizes.length > 0 && (
          <span style={{ marginLeft: "auto", fontSize: 11, color: "#333" }}>
            {config.lot_sizes.map(ls => `${ls.symbol_prefix} lot=${ls.lot_size}`).join(" · ")}
          </span>
        )}
      </div>

      {/* ── Loading spinner (initial only) ── */}
      {initialLoad && (
        <div style={{ padding: 60, textAlign: "center" }}>
          <div style={{
            width: 36, height: 36, borderRadius: "50%",
            border: "3px solid #1e1e2e", borderTop: "3px solid #4fc3f7",
            margin: "0 auto 14px", animation: "spin 0.8s linear infinite",
          }} />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          <div style={{ color: "#444", fontSize: 13 }}>Loading from Kite...</div>
        </div>
      )}

      {!initialLoad && (
        <>
          {/* ── Two account panels ── */}
          <div style={{ display: "flex", gap: 12, marginBottom: 16, alignItems: "stretch" }}>
            <AccountPanel
              title="Source — Kite1"
              accentColor="#4fc3f7"
              status={src1Status}
              loginUrl="/api/kite/login"
              positions={srcPos}
              orders={srcOrders}
              onRefreshStatus={fetchStatuses}
            />
            <AccountPanel
              title="Destination — Kite2"
              accentColor="#26a65b"
              status={kite2Status}
              loginUrl="/api/kite2/login"
              positions={dstPos}
              orders={dstOrders}
              onRefreshStatus={fetchStatuses}
            />
          </div>

          {/* ── Mirror log ── */}
          <div style={{ background: "#10101a", border: "1px solid #1e1e2e", borderRadius: 10 }}>
            <div style={{
              padding: "12px 16px", borderBottom: "1px solid #1a1a2e", background: "#0d0d1a",
              borderRadius: "10px 10px 0 0", fontSize: 13, fontWeight: 600, color: "#888",
            }}>
              🔁 Mirror Log ({mirrorMap.length})
            </div>
            <MirrorLogTable entries={mirrorMap} />
          </div>
        </>
      )}

      <div style={{ marginTop: 10, fontSize: 10, color: "#2a2a2a", textAlign: "right" }}>
        Backend poller · 15s interval · market hours 9:00–15:35 IST
      </div>
    </div>
  );
};

// ─── Styles ───────────────────────────────────────────────────────────────────

const tableStyle: React.CSSProperties = { width: "100%", borderCollapse: "collapse", fontSize: 12 };

const thStyle: React.CSSProperties = {
  padding: "9px 12px", textAlign: "left", fontSize: 10, fontWeight: 600,
  color: "#555", textTransform: "uppercase", letterSpacing: 0.5,
  borderBottom: "1px solid #1a1a2e", whiteSpace: "nowrap", background: "#0d0d1a",
};

const tdStyle: React.CSSProperties = {
  padding: "8px 12px", borderBottom: "1px solid #14141f", color: "#bbb", whiteSpace: "nowrap",
};

const rowAlt: React.CSSProperties = { background: "#0a0a14" };

const emptyStyle: React.CSSProperties = {
  padding: 24, textAlign: "center", color: "#444", fontSize: 13,
};

export default MirrorPage;
